import React, { useState, useMemo } from 'react';
import { CommentRecord } from 'shared/types/contentTypes';
import { useContentComments } from '../../hooks/useContentComments';
import { useAuth } from '../../hooks/useAuth';
import CommentCard from '../Comments/CommentCard/CommentCard';
import ReplyThread from '../Comments/ReplyThread/ReplyThread';
import NewCommentForm from '../Comments/NewCommentForm';

export interface ContentCommentSectionProps {
  contentId: string;
  contentType: string;
  contentTitle: string;
  hiddenParentCommentId?: string; // When provided, show replies to this comment as top-level
  placeholder?: string;
  buttonText?: string;
  className?: string;
  compact?: boolean;
  // UI customization options
  showNewCommentForm?: boolean;
  enableReplies?: boolean;
  showContentContext?: boolean;
  // Single thread mode: first comment is anchor, all subsequent comments become replies to it
  singleThreadMode?: boolean;
  // Use 'response' mode for gestures (Respond/Add a response instead of Contribute/Add a contribution)
  responseMode?: boolean;
  // Use text-first layout (text content first, author name as subtle metadata below)
  textFirstLayout?: boolean;
  // Always show author attribution (name under comment, "You" for own comments) - useful for conversation threads
  showAuthorAttribution?: boolean;
}

/**
 * Generic, reusable component for displaying and managing comments on any content
 * Supports the "hidden parent comment" pattern for specialized use cases like Listening Room
 */
const ContentCommentSection: React.FC<ContentCommentSectionProps> = ({
  contentId,
  contentType,
  contentTitle,
  hiddenParentCommentId,
  placeholder,
  buttonText,
  className = '',
  compact = false,
  showNewCommentForm = true,
  enableReplies = true,
  showContentContext = false,
  singleThreadMode = false,
  responseMode = false,
  textFirstLayout = false,
  showAuthorAttribution = false
}) => {
  const { currentUser2: currentUser } = useAuth();
  const [expandedComments, setExpandedComments] = useState<Record<string, boolean>>({});
  const [addingReplyToCommentId, setAddingReplyToCommentId] = useState<string | null>(null);

  // Use the content comments hook
  // In singleThreadMode, we need to fetch all comments including replies
  // so that getRepliesForComment can find actual DB replies to the anchor
  const {
    comments,
    isLoading,
    error,
    addComment,
    addReply,
    editComment,
    removeComment,
    likeComment
  } = useContentComments({
    contentId,
    contentType,
    contentTitle,
    hiddenParentCommentId,
    fetchComments: !!contentId,
    includeReplies: singleThreadMode // Fetch replies when in singleThreadMode
  });

  // Filter to get only top-level comments (no parentCommentId)
  const topLevelComments = useMemo(() => {
    if (hiddenParentCommentId) {
      // In hidden parent mode, all fetched comments are treated as top-level
      return comments;
    } else {
      // Normal mode: only show comments without a parent
      return comments.filter(comment => !comment.parentCommentId);
    }
  }, [comments, hiddenParentCommentId]);

  // In single-thread mode, the oldest top-level comment is the anchor
  // All other comments are replies to this anchor
  const anchorComment = useMemo(() => {
    if (!singleThreadMode || topLevelComments.length === 0) return null;
    // Sort by createdAt ascending to get the oldest (first) comment
    const sorted = [...topLevelComments].sort((a, b) =>
      new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );
    return sorted[0];
  }, [singleThreadMode, topLevelComments]);

  // In single-thread mode, hide the top-level form once an anchor exists
  const shouldShowTopLevelForm = showNewCommentForm &&
    (!singleThreadMode || topLevelComments.length === 0);

  // Helper to find replies to a comment
  const getRepliesForComment = (commentId: string): CommentRecord[] => {
    if (hiddenParentCommentId) {
      // In hidden parent mode, don't show nested replies
      return [];
    }

    // In single-thread mode, when asking for anchor's replies:
    // Include both actual replies AND other top-level comments (treated as replies to anchor)
    if (singleThreadMode && anchorComment && commentId === anchorComment.commentId) {
      // Get actual DB replies to anchor
      const actualReplies = comments.filter(c => c.parentCommentId === commentId);
      // Get other top-level comments (not the anchor itself)
      const otherTopLevel = topLevelComments.filter(c => c.commentId !== anchorComment.commentId);
      // Combine and sort by createdAt
      return [...actualReplies, ...otherTopLevel].sort((a, b) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      );
    }

    return comments.filter(comment => comment.parentCommentId === commentId);
  };

  // Function to toggle reply visibility
  const toggleReplies = (commentId: string) => {
    setExpandedComments(prev => ({
      ...prev,
      [commentId]: !prev[commentId]
    }));
  };

  // Handler for adding a new comment
  // In single-thread mode, if an anchor exists, add as reply to anchor
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleSubmitComment = async (text: string, _mentions?: unknown) => {
    if (singleThreadMode && anchorComment) {
      // Add as reply to anchor comment
      await addReply(text, anchorComment.commentId);
    } else {
      await addComment(text);
    }
  };

  // Handler for starting to add a reply
  const handleStartAddingReply = (commentId: string) => {
    if (!enableReplies) return;
    
    setAddingReplyToCommentId(commentId);
    // Auto-expand the comment thread when starting to add a reply
    setExpandedComments(prev => ({
      ...prev,
      [commentId]: true
    }));
  };

  // Handler for canceling adding a reply
  const handleCancelAddingReply = () => {
    setAddingReplyToCommentId(null);
  };

  // Handler for replying to a comment
  const handleReply = async (commentId: string, _contentIdParam: string, text: string): Promise<void> => {
    await addReply(text, commentId);
    
    // Auto-expand the comment thread that was replied to
    setExpandedComments(prev => ({
      ...prev,
      [commentId]: true
    }));
  };

  // Handler for editing a comment (simplified wrapper)
  const handleEditComment = async (commentId: string, text: string): Promise<void> => {
    await editComment(commentId, text);
  };

  // Handler for deleting a comment (simplified wrapper)
  const handleDeleteComment = async (commentId: string): Promise<void> => {
    await removeComment(commentId);
  };

  // Handler for liking a comment (simplified wrapper)
  const handleLikeComment = async (commentId: string, isLiked: boolean): Promise<void> => {
    await likeComment(commentId, isLiked);
  };

  // Error state
  if (error) {
    return (
      <div className={`text-center py-4 ${className}`}>
        <p className="text-red-500 text-sm">Failed to load comments. Please try again.</p>
      </div>
    );
  }

  return (
    <div className={`${className}`} data-testid="content-comment-section">
      {/* New comment form - hidden in single-thread mode once anchor exists */}
      {shouldShowTopLevelForm && (
        <NewCommentForm
          onSubmit={handleSubmitComment}
          placeholder={placeholder || (hiddenParentCommentId ?
            "Share your thoughts about this recommendation..." :
            "Share your perspective...")}
          buttonText={buttonText || (hiddenParentCommentId ? "Add Response" : "Add")}
        />
      )}
      
      {/* Loading state */}
      {isLoading && topLevelComments.length === 0 && (
        <div className="animate-pulse space-y-4 py-4">
          {[...Array(compact ? 1 : 3)].map((_, i) => (
            <div key={i} className="bg-white/90 backdrop-blur-sm rounded-2xl p-5 shadow-sm border border-gray-100/50">
              <div className="flex space-x-3">
                <div className="rounded-full bg-gray-200 h-10 w-10"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-3 bg-gray-200 rounded w-1/4"></div>
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Comments list */}
      <div className="space-y-4">
        {/* Show empty state */}
        {topLevelComments.length === 0 && !isLoading && (
          <div className={`text-center ${compact ? 'py-2 text-sm' : 'py-4 text-base'}`}>
            <p className="text-gray-500 font-light italic">
              {compact ? "No contributions yet." : "No contributions yet."}
            </p>
          </div>
        )}
        
        {/* Render comments */}
        {topLevelComments.length > 0 && (
          <div className="space-y-4">
            {(() => {
              // In single-thread mode, only show the anchor comment at top level
              // All other comments appear as replies to anchor
              const commentsToShow = singleThreadMode && anchorComment
                ? [anchorComment]
                : topLevelComments.sort((a, b) =>
                    new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
                  );

              return commentsToShow.map(comment => {
                // Find replies for this comment
                const replies = getRepliesForComment(comment.commentId);
                const isRepliesExpanded = expandedComments[comment.commentId] ?? true;

                // In single-thread mode, always enable replies on the anchor
                const shouldEnableReplies = singleThreadMode
                  ? (comment.commentId === anchorComment?.commentId)
                  : enableReplies;

                // In single-thread mode, show the top-level "Respond" button only when there are no replies
                // Once replies exist, the "Add a response" at bottom of thread becomes the CTA
                const showTopLevelReplyButton = singleThreadMode
                  ? replies.length === 0  // Show only if no replies in single-thread mode
                  : shouldEnableReplies;

                return (
                  <div key={comment.commentId} className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-sm border border-gray-100/50 overflow-hidden hover:shadow-md transition-all duration-300">
                    {/* Main Comment */}
                    <div className="p-5">
                      <CommentCard
                        comment={{
                          ...comment,
                          contentContext: {
                            contentId,
                            contentType,
                            contentTitle
                          }
                        }}
                        onLike={(commentId, isLiked) => handleLikeComment(commentId, isLiked)}
                        onEdit={(commentId, text) => handleEditComment(commentId, text)}
                        onDelete={(commentId) => handleDeleteComment(commentId)}
                        onStartAddingReply={showTopLevelReplyButton ? handleStartAddingReply : undefined}
                        currentUserId={currentUser?.userId}
                        showContentContext={showContentContext}
                        responseMode={responseMode}
                        textFirstLayout={textFirstLayout}
                        showAuthorAttribution={showAuthorAttribution}
                      />
                    </div>

                    {/* Reply Thread - show in normal mode or single-thread mode for anchor */}
                    {shouldEnableReplies && !hiddenParentCommentId && (
                      <ReplyThread
                        comment={{
                          ...comment,
                          contentContext: {
                            contentId,
                            contentType,
                            contentTitle
                          }
                        }}
                        contentId={contentId}
                        initialReplies={replies.map(reply => ({
                          ...reply,
                          contentContext: {
                            contentId,
                            contentType,
                            contentTitle
                          }
                        }))}
                        onReply={handleReply}
                        onLike={(commentId, isLiked) => handleLikeComment(commentId, isLiked)}
                        onEdit={(commentId, text) => handleEditComment(commentId, text)}
                        onDelete={(commentId) => handleDeleteComment(commentId)}
                        onCancelAddingReply={handleCancelAddingReply}
                        currentUserId={currentUser?.userId}
                        isExpanded={isRepliesExpanded}
                        isAddingReply={addingReplyToCommentId === comment.commentId}
                        onToggleExpand={() => toggleReplies(comment.commentId)}
                        showContentContext={showContentContext}
                        responseMode={responseMode}
                        showAuthorAttribution={showAuthorAttribution}
                      />
                    )}
                  </div>
                );
              });
            })()}
          </div>
        )}
      </div>
    </div>
  );
};

export default ContentCommentSection;